/* eslint-disable no-unused-vars */
import { forwardRef, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useSignal } from '@preact/signals-react';
import { CustomButton, Label, Bid, Loader, CheckboxInner } from 'components';
import { LampIcon } from 'assets/icons';
import { campaignsManageAPI } from 'services/campaignsManage.service';
import { saveCampaignServiceAPI } from 'services/saveCampaign.service';
import { manageCampaignsAPI } from 'services/manageCampaigns.service';
import { showPolipop, isBidErrorShown, isInvalidBid, trimDecimal } from 'utils';
import { ManageCampaignsContext } from 'contexts';
import { isAdminSelector } from 'store/selectors';
import * as S from './BidTooltip.styled';

export const BidTooltip = forwardRef((props, ref) => {
  const isAdmin = useSelector(isAdminSelector);

  const bidValue$ = useSignal(props.bidValue);
  const errorText$ = useSignal('');
  const isChecked$ = useSignal(true);
  const campaignId = props.data.campaignId;
  const [updateCampaignBid, result] = saveCampaignServiceAPI.useUpdateCampaignBidMutation();

  const {
    data: dataMinBid,
    isFetching,
    isLoading,
    isError,
  } = saveCampaignServiceAPI.useGetMinimumBidQuery(campaignId);

  //const [action, result] = manageCampaignsAPI.useLazyGetCampaignsByDateQuery();

  const dateForRequest = useSelector((state) => state.manageCampaigns.selectedDateRange);
  const { updateTableData } = useContext(ManageCampaignsContext);

  useEffect(() => {
    if (result.isSuccess || result.isError) {
      showPolipop(result);
    }
    if (result.isSuccess) {
      updateTableData({
        newValue: +bidValue$.value,
        fieldName: 'bid',
        campId: props.data.campaignId,
      });

      if (isChecked$.value) {
        updateTableData({
          newValue: 0,
          fieldName: 'is_has_custom_bid',
          campId: props.data.campaignId,
        });
      }
      props.isOpen$.value = false;
      //action(dateForRequest);
    }
  }, [result]);

  if (!props.data) return null;

  const isCpm = props.data.bid_type === 1;
  const minBid = isCpm ? window.cpm_bid_minimum : isAdmin ? window.bid_minimum_admin : dataMinBid;
  const maxBid = isCpm ? window.cpm_bid_maximum : window.bid_maximum;
  const isCheckbox = props.data.is_has_custom_bid == 1;
  const numApps = props.data.customBidApps;
  const isErrorShown = isBidErrorShown(bidValue$.value, minBid, maxBid); // isAdmin ? false : isBidErrorShown(bidValue$.value, minBid, maxBid);
  const isCheckboxChanged = isChecked$.value !== !!props.data.updateCustomBidApps;
  const isDisabled = isCheckbox
    ? isInvalidBid(bidValue$.value, props.bidValue, minBid, maxBid) &&
      !(bidValue$.value == props.bidValue)
    : isInvalidBid(bidValue$.value, props.bidValue, minBid, maxBid);

  const handleSave = () => {
    const bidToSave = bidValue$.value.toString().startsWith('.')
      ? trimDecimal('0' + bidValue$.value)
      : bidValue$.value;

    if (
      (isCheckbox &&
        isInvalidBid(bidToSave, props.bidValue, minBid, maxBid) &&
        !(isCheckboxChanged && bidValue$.value == props.bidValue)) ||
      (!isCheckbox && isInvalidBid(bidToSave, props.bidValue, minBid, maxBid))
    )
      return;

    const bidReq = {
      campaignId: props.data.campaignId,
      bid: bidToSave,
      updateCustomBidApps: +isChecked$.value,
    };
    updateCampaignBid(bidReq);
  };

  const handleBlur = (e) => {
    const currentBid = e.target.value;
    if (currentBid.startsWith('.')) {
      bidValue$.value = trimDecimal('0' + currentBid);
    }
  };

  const handleInputBid = (e) => {
    const inputValue = trimDecimal(e.target.value, isCpm);

    if (inputValue < minBid || inputValue === '.') errorText$.value = `Minimum bid is ${minBid}`;
    if (inputValue > maxBid) errorText$.value = `Maximum bid is ${maxBid}`;

    bidValue$.value = inputValue;
  };

  return (
    <div ref={ref} style={{ minWidth: '260px', padding: '16px' }}>
      {/* hardcoded */}
      <S.BidWrap>
        <Label text='Bid:' variant='label' style={{ marginBottom: '8px', lineHeight: '40px' }} />
        <Bid.Big value={bidValue$.value} onBlur={handleBlur} onChange={handleInputBid} />
        {isErrorShown && <S.ErrorHint>{errorText$.value}</S.ErrorHint>}
        {isFetching && <Loader.Local />}
        {/* <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
          <LampIcon />
          <S.Text>
            Minimum recommended
            <br /> Bid per location - <S.Strong>$5.5</S.Strong>
          </S.Text>
        </div> */}
      </S.BidWrap>
      {isCheckbox && (
        <S.CheckboxWrap>
          <CheckboxInner
            name='bid-checkbox'
            id='bid-checkbox'
            isChecked={isChecked$.value}
            onChange={() => (isChecked$.value = !isChecked$.value)}
          />
          <S.Text>{`Also update ${numApps} app${numApps > 1 ? 's' : ''} with custom bid`}</S.Text>
        </S.CheckboxWrap>
      )}
      <S.ButtonsWrap>
        <div style={{ flex: '1' }}>
          <CustomButton
            fullWidth
            id='close-changeBid-tooltip'
            disabled={result.isLoading}
            buttonColor={CustomButton.Colors?.Secondary}
            onClick={() => {
              props.isOpen$.value = false;
            }}
          >
            Cancel
          </CustomButton>
        </div>
        <div style={{ flex: '1' }}>
          <CustomButton
            fullWidth
            id='save--changeBid-tooltip'
            buttonColor={CustomButton.Colors?.Primary}
            onClick={handleSave}
            disabled={isDisabled || isErrorShown || result.isLoading || isError || isFetching}
          >
            Ok
          </CustomButton>
        </div>
      </S.ButtonsWrap>
    </div>
  );
});

BidTooltip.displayName = 'BidTooltip';
